/*
  ============================================================
  PIANZI ROLLOUT HEADER

  Handles:
  - Expanding menu rollout
  - Hamburger-to-minus state
  - Horizontal tab switching
  - Keyboard tab navigation
  - Inline search rollout
  - Shopify-rendered predictive search
  - Live cart quantity
  - Dawn cart-drawer compatibility
  - Shopify theme-editor reloads
  ============================================================
*/

(() => {
  'use strict';

  const HEADER_SELECTOR = '[data-pianzi-rollout]';
  const SEARCH_DELAY = 250;
  const MINIMUM_SEARCH_LENGTH = 2;

  class PianziRolloutHeader {
    constructor(header) {
      this.header = header;

      /*
        Menu elements
      */

      this.menuToggle = header.querySelector(
        '[data-pianzi-menu-toggle]'
      );

      this.menuRollout = header.querySelector(
        '[data-pianzi-menu-rollout]'
      );

      this.tabs = Array.from(
        header.querySelectorAll(
          '[data-pianzi-tab]'
        )
      );

      this.panels = Array.from(
        header.querySelectorAll(
          '[data-pianzi-panel]'
        )
      );

      /*
        Search elements
      */

      this.searchToggle = header.querySelector(
        '[data-pianzi-search-toggle]'
      );

      this.searchRollout = header.querySelector(
        '[data-pianzi-search-rollout]'
      );

      this.searchClose = header.querySelector(
        '[data-pianzi-search-close]'
      );

      this.searchForm = header.querySelector(
        '[data-pianzi-search-form]'
      );

      this.searchInput = header.querySelector(
        '[data-pianzi-search-input]'
      );

      this.predictiveResults = header.querySelector(
        '[data-pianzi-predictive-results]'
      );

      this.predictiveInner = header.querySelector(
        '[data-pianzi-predictive-inner]'
      );

      /*
        Cart elements
      */

      this.cartButton = header.querySelector(
        '[data-pianzi-cart-button]'
      );

      this.cartCountBadge = header.querySelector(
        '[data-pianzi-cart-count]'
      );

      this.cartCountText = header.querySelector(
        '[data-pianzi-cart-count-text]'
      );

      /*
        Route data supplied by Liquid
      */

      this.predictiveSearchUrl =
        header.dataset.predictiveSearchUrl || '';

      this.searchUrl =
        header.dataset.searchUrl || '';

      this.cartJsonUrl =
        header.dataset.cartJsonUrl || '';

      this.hideZeroCount =
        header.dataset.hideZeroCount === 'true';

      this.internalHeaderClearance =
        Number(header.dataset.internalHeaderClearance || 116);

      this.mobileInternalHeaderClearance =
        Number(header.dataset.mobileInternalHeaderClearance || 100);

      /*
        Internal state
      */

      this.menuIsOpen = false;
      this.searchIsOpen = false;

      this.searchTimer = null;
      this.cartTimer = null;

      this.searchAbortController = null;
      this.latestSearchTerm = '';

      this.lastFocusedElement = null;

      this.boundHandleDocumentKeydown =
        this.handleDocumentKeydown.bind(this);

      this.boundHandleDocumentClick =
        this.handleDocumentClick.bind(this);

      this.boundHandleSearchInput =
        this.handleSearchInput.bind(this);

      this.boundHandleCartUpdate =
        this.handleCartUpdate.bind(this);

      this.init();
    }

    init() {
      this.setupPageClearance();
      this.setupMenu();
      this.setupTabs();
      this.setupSearch();
      this.setupCart();

      this.subscribeToCartEvents();
      this.observeCartDrawer();

      document.addEventListener(
        'keydown',
        this.boundHandleDocumentKeydown
      );

      document.addEventListener(
        'click',
        this.boundHandleDocumentClick
      );

      this.refreshCartCount();

      this.header.pianziRolloutInstance = this;
    }


    setupPageClearance() {
      const isHomepage =
        this.header.classList.contains('pianzi-rollout--homepage');

      const needsContentClearance =
        !isHomepage;

      document.body.classList.toggle(
        'pianzi-needs-overlay-content-clearance',
        needsContentClearance
      );

      document.body.classList.remove(
        'pianzi-has-internal-rollout-header'
      );

      document.body.classList.toggle(
        'pianzi-page-has-search-layout',
        Boolean(document.querySelector('.pz-search-page'))
      );

      document.body.classList.toggle(
        'pianzi-page-has-product-layout',
        Boolean(document.querySelector('.pz-product-main'))
      );

      document.documentElement.style.setProperty(
        '--pianzi-overlay-content-clearance',
        needsContentClearance ? `${this.internalHeaderClearance}px` : '0px'
      );

      document.documentElement.style.setProperty(
        '--pianzi-mobile-overlay-content-clearance',
        needsContentClearance ? `${this.mobileInternalHeaderClearance}px` : '0px'
      );
    }

    /*
      ============================================================
      MENU ROLLOUT
      ============================================================
    */

    setupMenu() {
      if (
        !this.menuToggle ||
        !this.menuRollout
      ) {
        return;
      }

      this.menuToggle.addEventListener(
        'click',
        () => {
          if (this.menuIsOpen) {
            this.closeMenu();
          } else {
            this.openMenu();
          }
        }
      );
    }

    openMenu() {
      if (
        !this.menuRollout ||
        this.menuIsOpen
      ) {
        return;
      }

      this.closeSearch(false);

      this.menuIsOpen = true;
      this.lastFocusedElement =
        document.activeElement;

      this.header.classList.add(
        'is-menu-open'
      );

      this.menuRollout.setAttribute(
        'aria-hidden',
        'false'
      );

      this.menuToggle?.setAttribute(
        'aria-expanded',
        'true'
      );

      this.menuToggle?.setAttribute(
        'aria-label',
        'Close menu'
      );

      /*
        Ensure the active panel is visible after the card
        begins expanding.
      */

      const activePanel = this.header.querySelector(
        '[data-pianzi-panel].is-active'
      );

      if (activePanel) {
        activePanel.hidden = false;
      }
    }

    closeMenu(restoreFocus = true) {
      if (
        !this.menuRollout ||
        !this.menuIsOpen
      ) {
        return;
      }

      this.menuIsOpen = false;

      this.header.classList.remove(
        'is-menu-open'
      );

      this.menuRollout.setAttribute(
        'aria-hidden',
        'true'
      );

      this.menuToggle?.setAttribute(
        'aria-expanded',
        'false'
      );

      this.menuToggle?.setAttribute(
        'aria-label',
        'Open menu'
      );

      if (
        restoreFocus &&
        this.lastFocusedElement instanceof HTMLElement
      ) {
        this.lastFocusedElement.focus({
          preventScroll: true
        });
      }

      this.lastFocusedElement = null;
    }

    /*
      ============================================================
      TABS
      ============================================================
    */

    setupTabs() {
      if (!this.tabs.length) return;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener(
          'click',
          () => {
            this.activateTab(index, true);
          }
        );

        tab.addEventListener(
          'keydown',
          (event) => {
            this.handleTabKeydown(
              event,
              index
            );
          }
        );
      });
    }

    activateTab(index, moveFocus = false) {
      const selectedTab = this.tabs[index];
      const selectedPanel =
        this.panels[index];

      if (
        !selectedTab ||
        !selectedPanel
      ) {
        return;
      }

      this.tabs.forEach((tab, tabIndex) => {
        const isSelected =
          tabIndex === index;

        tab.classList.toggle(
          'is-active',
          isSelected
        );

        tab.setAttribute(
          'aria-selected',
          isSelected ? 'true' : 'false'
        );

        tab.setAttribute(
          'tabindex',
          isSelected ? '0' : '-1'
        );
      });

      this.panels.forEach(
        (panel, panelIndex) => {
          const isSelected =
            panelIndex === index;

          panel.classList.toggle(
            'is-active',
            isSelected
          );

          panel.hidden = !isSelected;
        }
      );

      if (moveFocus) {
        selectedTab.focus({
          preventScroll: true
        });
      }
    }

    handleTabKeydown(event, index) {
      const lastIndex =
        this.tabs.length - 1;

      let nextIndex = null;

      switch (event.key) {
        case 'ArrowRight':
          nextIndex =
            index === lastIndex
              ? 0
              : index + 1;
          break;

        case 'ArrowLeft':
          nextIndex =
            index === 0
              ? lastIndex
              : index - 1;
          break;

        case 'Home':
          nextIndex = 0;
          break;

        case 'End':
          nextIndex = lastIndex;
          break;

        default:
          return;
      }

      event.preventDefault();

      this.activateTab(
        nextIndex,
        true
      );

      this.tabs[nextIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }

    /*
      ============================================================
      SEARCH ROLLOUT
      ============================================================
    */

    setupSearch() {
      if (
        !this.searchToggle ||
        !this.searchRollout ||
        !this.searchInput
      ) {
        return;
      }

      this.searchToggle.addEventListener(
        'click',
        () => {
          if (this.searchIsOpen) {
            this.closeSearch();
          } else {
            this.openSearch();
          }
        }
      );

      this.searchClose?.addEventListener(
        'click',
        () => {
          this.closeSearch();
        }
      );

      this.searchInput.addEventListener(
        'input',
        this.boundHandleSearchInput
      );

      this.searchInput.addEventListener(
        'focus',
        () => {
          const term =
            this.searchInput.value.trim();

          if (
            term.length >=
              MINIMUM_SEARCH_LENGTH &&
            this.predictiveInner?.innerHTML.trim()
          ) {
            this.showPredictiveResults();
          }
        }
      );

      this.searchForm?.addEventListener(
        'submit',
        (event) => {
          const term =
            this.searchInput.value.trim();

          if (!term) {
            event.preventDefault();

            this.searchInput.focus({
              preventScroll: true
            });
          }
        }
      );
    }

    openSearch() {
      if (
        !this.searchRollout ||
        this.searchIsOpen
      ) {
        return;
      }

      this.closeMenu(false);

      this.searchIsOpen = true;
      this.lastFocusedElement =
        document.activeElement;

      this.header.classList.add(
        'is-search-open'
      );

      this.searchRollout.setAttribute(
        'aria-hidden',
        'false'
      );

      this.searchToggle?.setAttribute(
        'aria-expanded',
        'true'
      );

      requestAnimationFrame(() => {
        this.searchInput?.focus({
          preventScroll: true
        });
      });
    }

    closeSearch(restoreFocus = true) {
      if (
        !this.searchRollout ||
        !this.searchIsOpen
      ) {
        return;
      }

      this.searchIsOpen = false;

      this.header.classList.remove(
        'is-search-open',
        'is-search-loading'
      );

      this.searchRollout.setAttribute(
        'aria-hidden',
        'true'
      );

      this.searchToggle?.setAttribute(
        'aria-expanded',
        'false'
      );

      this.hidePredictiveResults();
      this.abortSearchRequest();

      if (
        restoreFocus &&
        this.lastFocusedElement instanceof HTMLElement
      ) {
        this.lastFocusedElement.focus({
          preventScroll: true
        });
      }

      this.lastFocusedElement = null;
    }

    handleSearchInput() {
      const term =
        this.searchInput?.value.trim() || '';

      window.clearTimeout(
        this.searchTimer
      );

      if (
        term.length <
        MINIMUM_SEARCH_LENGTH
      ) {
        this.latestSearchTerm = '';

        this.abortSearchRequest();
        this.clearPredictiveResults();

        return;
      }

      this.searchTimer =
        window.setTimeout(() => {
          this.fetchPredictiveResults(
            term
          );
        }, SEARCH_DELAY);
    }

    abortSearchRequest() {
      if (this.searchAbortController) {
        this.searchAbortController.abort();
        this.searchAbortController = null;
      }
    }

    async fetchPredictiveResults(term) {
      if (
        !this.predictiveSearchUrl ||
        !this.predictiveInner
      ) {
        return;
      }

      if (
        term === this.latestSearchTerm &&
        this.predictiveInner.innerHTML.trim()
      ) {
        this.showPredictiveResults();
        return;
      }

      this.abortSearchRequest();

      this.searchAbortController =
        new AbortController();

      const parameters =
        new URLSearchParams();

      parameters.set('q', term);

      /*
        Shopify renders the section file created below.
      */

      parameters.set(
        'section_id',
        'pianzi-predictive-search'
      );

      parameters.set(
        'resources[type]',
        'product'
      );

      parameters.set(
        'resources[limit]',
        '8'
      );

      parameters.set(
        'resources[limit_scope]',
        'each'
      );

      parameters.set(
        'resources[options][unavailable_products]',
        'last'
      );

      const requestUrl =
        `${this.predictiveSearchUrl}?${parameters.toString()}`;

      this.setSearchLoading(true);

      try {
        const response = await fetch(
          requestUrl,
          {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
              Accept: 'text/html',
              'X-Requested-With':
                'XMLHttpRequest'
            },
            signal:
              this.searchAbortController.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            `Predictive search returned ${response.status}`
          );
        }

        const html =
          await response.text();

        /*
          Prevent an older request from replacing results
          for a newer search term.
        */

        if (
          this.searchInput?.value.trim() !==
          term
        ) {
          return;
        }

        this.latestSearchTerm = term;

        this.renderPredictiveHtml(
          html,
          term
        );
      } catch (error) {
        if (
          error.name === 'AbortError'
        ) {
          return;
        }

        console.warn(
          'PIANZI predictive-search error:',
          error
        );

        this.renderSearchFallback(term);
      } finally {
        this.setSearchLoading(false);
      }
    }

    renderPredictiveHtml(html, term) {
      if (!this.predictiveInner) return;

      const parser =
        new DOMParser();

      const documentFragment =
        parser.parseFromString(
          html,
          'text/html'
        );

      const renderedResults =
        documentFragment.querySelector(
          '[data-pianzi-rendered-predictive]'
        ) ||
        documentFragment.body?.querySelector(
          '[data-pianzi-rendered-predictive]'
        );

      if (!renderedResults) {
        this.renderSearchFallback(term);
        return;
      }

      this.predictiveInner.innerHTML =
        renderedResults.innerHTML;

      this.showPredictiveResults();
    }

    renderSearchFallback(term) {
      if (!this.predictiveInner) return;

      const safeTerm =
        this.escapeHtml(term);

      const searchHref =
        `${this.searchUrl}?q=${encodeURIComponent(term)}`;

      this.predictiveInner.innerHTML = `
        <div class="pianzi-rollout__predictive-empty">
          <p>
            Continue searching PIANZI for “${safeTerm}”.
          </p>

          <a
            class="pianzi-rollout__predictive-all-link"
            href="${this.escapeAttribute(searchHref)}"
          >
            <span>View all results</span>
            <span aria-hidden="true">→</span>
          </a>
        </div>
      `;

      this.showPredictiveResults();
    }

    showPredictiveResults() {
      if (
        !this.predictiveResults ||
        !this.searchInput
      ) {
        return;
      }

      this.predictiveResults.hidden = false;

      this.searchInput.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    hidePredictiveResults() {
      this.predictiveResults?.setAttribute(
        'hidden',
        ''
      );

      this.searchInput?.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    clearPredictiveResults() {
      if (this.predictiveInner) {
        this.predictiveInner.innerHTML = '';
      }

      this.hidePredictiveResults();
    }

    setSearchLoading(isLoading) {
      this.header.classList.toggle(
        'is-search-loading',
        isLoading
      );

      this.searchInput?.setAttribute(
        'aria-busy',
        isLoading ? 'true' : 'false'
      );
    }

    /*
      ============================================================
      CART
      ============================================================
    */

    setupCart() {
      if (!this.cartButton) return;

      this.cartButton.addEventListener(
        'click',
        (event) => {
          const cartDrawer =
            document.querySelector(
              'cart-drawer'
            );
            /*
  Cart removal and plus/minus controls do not always emit a
  normal change event, so refresh after those controls are used.
*/

document.addEventListener(
  'click',
  (event) => {
    const cartControl =
      event.target.closest?.(
        [
          'cart-remove-button',
          '.cart-remove-button',
          '.quantity__button',
          '[name="minus"]',
          '[name="plus"]',
          '[data-cart-remove]',
          '[data-quantity-minus]',
          '[data-quantity-plus]'
        ].join(',')
      );

    if (!cartControl) return;

    /*
      Dawn updates the cart asynchronously. The first refresh
      catches quick responses, while the second confirms the
      final cart state after the drawer finishes rendering.
    */

    this.scheduleCartRefresh(300);

    window.setTimeout(() => {
      this.refreshCartCount();
    }, 850);
  },
  true
);

          /*
            Use Dawn's real cart drawer when it exists.
            The normal cart URL remains the fallback.
          */

          if (
            cartDrawer &&
            typeof cartDrawer.open ===
              'function'
          ) {
            event.preventDefault();

            try {
              cartDrawer.open(
                this.cartButton
              );
            } catch (error) {
              window.location.href =
                this.cartButton.href;
            }
          }
        }
      );

      /*
        Catch standard product forms.
      */

      document.addEventListener(
        'submit',
        (event) => {
          const form =
            event.target.closest?.(
              'form[action*="/cart/add"]'
            );

          if (form) {
            this.scheduleCartRefresh(
              500
            );
          }
        },
        true
      );

      /*
        Catch quantity changes inside common Dawn cart
        components.
      */

      document.addEventListener(
        'change',
        (event) => {
          if (
            event.target.closest?.(
              'cart-drawer, cart-items, .cart-items'
            )
          ) {
            this.scheduleCartRefresh(
              350
            );
          }
        },
        true
      );
    }

    subscribeToCartEvents() {
  /*
        Use Dawn's pub/sub system when available.
  */

  if (
        typeof window.subscribe ===
          'function' &&
        window.PUB_SUB_EVENTS?.cartUpdate
  ) {
    try {
      this.unsubscribeCart =
            window.subscribe(
              window.PUB_SUB_EVENTS.cartUpdate,
              this.boundHandleCartUpdate
        );
    } catch (error) {
      console.warn(
        'PIANZI could not subscribe to Dawn cart updates:',
        error
      );
    }
  }

  /*
        Compatibility with common custom-theme events.
  */

  [
    'cart:updated',
    'cart:update',
    'cart:refresh',
    'cart-change',
    'product:added'
  ].forEach((eventName) => {
    document.addEventListener(
      eventName,
      this.boundHandleCartUpdate
    );
  });
}

    handleCartUpdate(event) {
  /*
    Dawn's pub/sub callback passes its payload directly:

    {
      source: 'cart-items',
      cartData: {
        item_count: 0,
        ...
      }
    }

    Custom events may still place data inside event.detail,
    so we support both formats.
  */

  const eventCount =
    event?.cartData?.item_count ??
    event?.cart?.item_count ??
    event?.item_count ??
    event?.itemCount ??
    event?.detail?.cartData?.item_count ??
    event?.detail?.cart?.item_count ??
    event?.detail?.data?.item_count ??
    event?.detail?.data?.itemCount ??
    event?.detail?.item_count ??
    event?.detail?.itemCount ??
    null;

  if (
    eventCount !== null &&
    eventCount !== undefined
  ) {
    this.updateCartCount(
      Number(eventCount)
    );
  }

  /*
    Also verify against Shopify's cart endpoint after Dawn
    finishes rendering. This keeps the badge accurate when
    an item is removed, quantity becomes zero, or a custom
    cart component publishes an incomplete event.
  */

  this.scheduleCartRefresh(180);
}

    scheduleCartRefresh(delay = 150) {
      window.clearTimeout(
        this.cartTimer
      );

      this.cartTimer =
        window.setTimeout(() => {
          this.refreshCartCount();
        }, delay);
    }

    async refreshCartCount() {
      if (!this.cartJsonUrl) return;

      try {
        const response = await fetch(
          this.cartJsonUrl,
          {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'X-Requested-With':
                'XMLHttpRequest'
            }
          }
        );

        if (!response.ok) {
          throw new Error(
            `Cart request returned ${response.status}`
          );
        }

        const cart =
          await response.json();

        this.updateCartCount(
          Number(cart.item_count || 0)
        );
      } catch (error) {
        console.warn(
          'PIANZI cart-count refresh error:',
          error
        );
      }
    }

    updateCartCount(itemCount) {
      const safeCount =
        Number.isFinite(itemCount)
          ? Math.max(0, itemCount)
          : 0;

      if (this.cartCountBadge) {
        this.cartCountBadge.textContent =
          String(safeCount);

        this.cartCountBadge.classList.toggle(
          'is-hidden',
          this.hideZeroCount &&
            safeCount === 0
        );
      }

      if (this.cartCountText) {
        this.cartCountText.textContent =
          `(${safeCount})`;
      }

      this.cartButton?.setAttribute(
        'aria-label',
        `Cart with ${safeCount} ${
          safeCount === 1
            ? 'item'
            : 'items'
        }`
      );
    }

    observeCartDrawer() {
      const cartDrawer =
        document.querySelector(
          'cart-drawer'
        );

      if (!cartDrawer) return;

      this.cartObserver =
        new MutationObserver(() => {
          this.scheduleCartRefresh(120);
        });

      this.cartObserver.observe(
        cartDrawer,
        {
          childList: true,
          subtree: true,
          characterData: true
        }
      );
    }

    /*
      ============================================================
      DOCUMENT EVENTS
      ============================================================
    */

    handleDocumentKeydown(event) {
      if (event.key !== 'Escape') {
        return;
      }

      if (this.searchIsOpen) {
        this.closeSearch();
        return;
      }

      if (this.menuIsOpen) {
        this.closeMenu();
      }
    }

    handleDocumentClick(event) {
      if (
        !this.header.contains(
          event.target
        )
      ) {
        if (this.searchIsOpen) {
          this.closeSearch(false);
        }

        if (
          this.menuIsOpen &&
          this.header.dataset
            .closeMenuOutside === 'true'
        ) {
          this.closeMenu(false);
        }
      }
    }

    /*
      ============================================================
      HELPERS
      ============================================================
    */

    escapeHtml(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }

    /*
      ============================================================
      CLEANUP FOR SHOPIFY THEME EDITOR
      ============================================================
    */

    destroy() {
      window.clearTimeout(
        this.searchTimer
      );

      window.clearTimeout(
        this.cartTimer
      );

      this.abortSearchRequest();

      document.removeEventListener(
        'keydown',
        this.boundHandleDocumentKeydown
      );

      document.removeEventListener(
        'click',
        this.boundHandleDocumentClick
      );

      [
        'cart:updated',
        'cart:update',
        'cart:refresh',
        'cart-change',
        'product:added'
      ].forEach((eventName) => {
        document.removeEventListener(
          eventName,
          this.boundHandleCartUpdate
        );
      });

      if (
        typeof this.unsubscribeCart ===
        'function'
      ) {
        this.unsubscribeCart();
      }

      this.cartObserver?.disconnect();

      delete this.header
        .pianziRolloutInstance;
    }
  }

  /*
    ============================================================
    INITIALIZATION
    ============================================================
  */

  const initializeHeaders = (
    root = document
  ) => {
    const headers = [];

    if (
      root.matches?.(
        HEADER_SELECTOR
      )
    ) {
      headers.push(root);
    }

    root
      .querySelectorAll?.(
        HEADER_SELECTOR
      )
      .forEach((header) => {
        headers.push(header);
      });

    headers.forEach((header) => {
      if (
        !header.pianziRolloutInstance
      ) {
        new PianziRolloutHeader(
          header
        );
      }
    });
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeHeaders();
      },
      { once: true }
    );
  } else {
    initializeHeaders();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeHeaders(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const header =
        event.target.matches?.(
          HEADER_SELECTOR
        )
          ? event.target
          : event.target.querySelector?.(
              HEADER_SELECTOR
            );

      header
        ?.pianziRolloutInstance
        ?.destroy();
    }
  );

  document.addEventListener(
    'shopify:section:select',
    (event) => {
      initializeHeaders(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const header =
        event.target.closest?.(
          HEADER_SELECTOR
        );

      const tabBlock =
        event.target.matches?.(
          '[data-pianzi-tab]'
        )
          ? event.target
          : event.target.querySelector?.(
              '[data-pianzi-tab]'
            );

      if (
        !header ||
        !tabBlock
      ) {
        return;
      }

      const instance =
        header.pianziRolloutInstance;

      const index =
        Number(
          tabBlock.dataset.pianziTab
        );

      instance?.openMenu();
      instance?.activateTab(
        index,
        false
      );
    }
  );
})();